import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { Modal } from "react-bootstrap";
import { useAcademicYear } from "../../../core/common/context/AcademicYearContext";
import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { Link } from "react-router-dom";
import CreateAccountModal from "./createAccountModal";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import EditAccountModal from "./editAccountModal";
import {
  getPaymentSettingsByYear,
  updatePaymentSettingsById,
} from "../../../api/paiement_settings";
import { toast } from "sonner";
import {
  deleteBankAccount,
  getBankAccountsByPaymentSettingId,
} from "../../../api/bank_account";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

type Props = {
  show: boolean;
  setShow: (val: boolean) => void;
  onClose: () => void;
  onSaved?: () => void;
};

// Boutton protéger pour ajouter un compte bancaire
const ProtectedBtnAddAccount = withPermission(
  (props: any) => (
    <button
      className="btn btn-primary btn-sm"
      onClick={() => {
        props.onClose();
        props.setShowCreate(true);
      }}
    >
      <i className="ti ti-plus me-1" /> Nouveau
    </button>
  ),
  [buildPermission(Resource.PAYMENT_SETTING, Action.UPDATE)],
  "masquer",
);

// Boutton protéger pour modifier un compte bancaire
const ProtectedBtnUpdateAccount = withPermission(
  (props: any) => (
    <button
      className="dropdown-item rounded-1"
      onClick={() => {
        props.setSelectedAccount(props.account);
        props.setShow(false);
        props.setShowEdit(true);
      }}
    >
      <i className="ti ti-menu me-2" /> Modifier
    </button>
  ),
  [buildPermission(Resource.PAYMENT_SETTING, Action.UPDATE)],
  "masquer",
);

// Boutton protéger pour supprimer un compte bancaire
const ProtectedBtnDeleteAccount = withPermission(
  (props: any) => (
    <button
      className="dropdown-item rounded-1"
      onClick={() => props.showConfirmationAlert(props.account)}
    >
      <i className="ti ti-trash-x me-2" /> Supprimer
    </button>
  ),
  [buildPermission(Resource.PAYMENT_SETTING, Action.UPDATE)],
  "masquer",
);

// Boutton protéger pour modifier les paramètres de paiement
const ProtectedBtnUpdateSettingsCurrency = withPermission(
  (props: any) => (
    <button
      className="btn btn-outline-primary"
      onClick={() => props.onSubmitCurrency()}
    >
      Valider
    </button>
  ),
  [buildPermission(Resource.PAYMENT_SETTING, Action.UPDATE)],
  "masquer",
);

// Boutton protéger pour modifier les paramètres de paiement
const ProtectedBtnUpdateSettingsSignatory = withPermission(
  (props: any) => (
    <button className="btn btn-outline-primary btn-sm" type="submit">
      {props.signatorySubmitting ? "Enregistrement..." : "Enregistrer"}
    </button>
  ),
  [buildPermission(Resource.PAYMENT_SETTING, Action.UPDATE)],
  "masquer",
);

const PaymentSettingsModal = ({ show, setShow, onClose, onSaved }: Props) => {
  //Initialisation de l'année academmique actuelle
  const { selectedYear } = useAcademicYear();

  const [paymentSettings, setPaymentSettings] = useState<any>();
  const [bankAccounts, setBankAccounts] = useState<any>([]);
  const [selectedAccount, setSelectedAccount] = useState<any>();
  const [currency, setCurrency] = useState<string>("");

  const [loadingAccounts, setLoadingAccounts] = useState<boolean>(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [settingsView, setSettingsView] = useState<"payment" | "signatories">(
    "payment",
  );

  const signatorySchema = z.object({
    bourseSignatory1: z.string().optional(),
    bourseSignatory1Post: z.string().optional(),
    bourseSignatory2: z.string().optional(),
    bourseSignatory2Post: z.string().optional(),
    receiptSignatory1: z.string().optional(),
    receiptSignatory1Post: z.string().optional(),
    annualReportBourseSignatory1: z.string().optional(),
    annualReportBourseSignatory1Post: z.string().optional(),
    financialAnnualReportSignatory1: z.string().optional(),
    financialAnnualReportSignatory1Post: z.string().optional(),
  });

  type SignatoryFields = z.infer<typeof signatorySchema>;

  const {
    register: registerSignatory,
    handleSubmit: handleSubmitSignatory,
    setValue,
    formState: { errors: signatoryErrors, isSubmitting: signatorySubmitting },
  } = useForm<SignatoryFields>({
    resolver: zodResolver(signatorySchema),
    defaultValues: {
      bourseSignatory1: "",
      bourseSignatory1Post: "",
      bourseSignatory2: "",
      bourseSignatory2Post: "",
      receiptSignatory1: "",
      receiptSignatory1Post: "",
      annualReportBourseSignatory1: "",
      annualReportBourseSignatory1Post: "",
      financialAnnualReportSignatory1: "",
      financialAnnualReportSignatory1Post: "",
    },
  });

  //Reload de la liste
  const reloadAccountData = () => setRefreshKey((prev) => prev + 1);

  // fonction pour récupérer les paramêtres de paiement de l'année academique
  const fetchPaymentParameters = async () => {
    try {
      if (!selectedYear) return;
      const res = await getPaymentSettingsByYear(selectedYear?.id);
      // console.log("Parameters: ", res.data);
      setPaymentSettings(res?.data[0]);
      setCurrency(res?.data[0]?.currency || "");
    } catch (error: any) {
      // console.log("Error fetching payment parameters:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  // récupérer tous les comptes bancaire du paramêtre de paiement
  const fetchBankAccountsByPaymentSettingId = async (
    id_paymentSetting: any,
  ) => {
    try {
      if (!id_paymentSetting) return;
      setLoadingAccounts(true);
      const res = await getBankAccountsByPaymentSettingId(
        id_paymentSetting,
        buildPermissionContext(Resource.PAYMENT_SETTING, Action.READ),
      );
      // console.log("Bank Accounts: ", res.data);
      setBankAccounts(res?.data || []);
    } catch (error: any) {
      // console.log("Error fetching bank accounts:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    } finally {
      setLoadingAccounts(false);
    }
  };

  // charger les paramètres de paiement de l'annéesélectionné
  useEffect(() => {
    if (!show) return;
    fetchPaymentParameters();
    setSettingsView("payment");
  }, [show, selectedYear]);

  // charger les comptes des paramètres de paiement
  useEffect(() => {
    if (!paymentSettings) return;
    fetchBankAccountsByPaymentSettingId(paymentSettings?.id);
    setValue("bourseSignatory1", paymentSettings?.signatoryFicheBourse1 || "");
    setValue(
      "bourseSignatory1Post",
      paymentSettings?.postSignatoryFicheBourse1 || "",
    );
    setValue("bourseSignatory2", paymentSettings?.signatoryFicheBourse2 || "");
    setValue(
      "bourseSignatory2Post",
      paymentSettings?.postSignatoryFicheBourse2 || "",
    );
    setValue("receiptSignatory1", paymentSettings?.signatoryRecu || "");
    setValue("receiptSignatory1Post", paymentSettings?.postSignatoryRecu || "");

    setValue(
      "annualReportBourseSignatory1",
      paymentSettings?.signatoryRapportAnnuelBourse || "",
    );
    setValue(
      "annualReportBourseSignatory1Post",
      paymentSettings?.postSignatoryRapportAnnuelBourse || "",
    );
    setValue(
      "financialAnnualReportSignatory1",
      paymentSettings?.signatoryRapportFinancier || "",
    );
    setValue(
      "financialAnnualReportSignatory1Post",
      paymentSettings?.postSignatoryRapportFinancier || "",
    );
  }, [paymentSettings]);

  //Initialisation de sweetAlert
  const MySwal = withReactContent(Swal);

  //Traitement en alert de la demande de réinitialisation de mot de passe
  const showConfirmationAlert = (account: any) => {
    MySwal.fire({
      title: "Supprimer un Compte Bancaire ?",
      text: `Souhaitez-vous vraiment supprimer le compte bancaire ${account.account_title} ?`,
      showCancelButton: true,
      // confirmButtonColor: "#00ff00",
      confirmButtonText: "Envoyer!",
      // cancelButtonColor: "#ff0000",
      cancelButtonText: "Annuler",
    }).then(async (result: any) => {
      if (result.isConfirmed) {
        // ── Affiche le spinner pendant la requête ──
        MySwal.fire({
          title: "Suppression en cours...",
          allowOutsideClick: false,
          allowEscapeKey: false,
          showConfirmButton: false,
          didOpen: () => MySwal.showLoading(),
        });
        try {
          //On lance la requête d'envoi de mail
          await deleteBankAccount(account.id);
          MySwal.fire({
            title: "Compte bancaire supprimé avec succès !",
            icon: "success",
            confirmButtonText: "OK",
          });

          fetchBankAccountsByPaymentSettingId(paymentSettings?.id); //On reload la liste
        } catch (err: any) {
          //On affiche une alerte en cas d'erreur côté serveur
          MySwal.fire({
            title: "Erreur",
            text:
              err.response?.data?.message ||
              err.response?.data?.error ||
              "Une erreur est survenue, réessayez plus tard",
            icon: "error",
            confirmButtonText: "OK",
          });
        }
      }
    });
  };

  // fonction pour valider la device (currency)
  const onSubmitCurrency = async () => {
    try {
      if (!currency) {
        toast.error("Veuillez entrer une devise.");
        return;
      }
      await updatePaymentSettingsById(paymentSettings?.id, { currency });
      toast.success("Devise mise à jour avec succès !");
    } catch (err: any) {
      // console.log("Erreur lors de la mise à jour de la devise: ", err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Une erreur est survenue, réessayez plus tard",
      );
    }
  };

  // fonction pour enregistrer les signataires
  const onSubmitSignatories = async (data: SignatoryFields) => {
    try {
      const payload = {
        postSignatoryRecu: data.receiptSignatory1Post,
        signatoryRecu: data.receiptSignatory1,
        postSignatoryFicheBourse1: data.bourseSignatory1Post,
        postSignatoryFicheBourse2: data.bourseSignatory2Post,
        signatoryFicheBourse1: data.bourseSignatory1,
        signatoryFicheBourse2: data.bourseSignatory2,
        postSignatoryRapportAnnuelBourse: data.annualReportBourseSignatory1Post,
        signatoryRapportAnnuelBourse: data.annualReportBourseSignatory1,
        postSignatoryRapportFinancier: data.financialAnnualReportSignatory1Post,
        signatoryRapportFinancier: data.financialAnnualReportSignatory1,
      };

      await updatePaymentSettingsById(paymentSettings?.id, payload);
      toast.success("Signataires mis à jour avec succès !");
    } catch (err: any) {
      // console.log("Erreur lors de la mise à jour des signataires: ", err);
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Une erreur est survenue, réessayez plus tard",
      );
    }
  };

  return (
    <>
      <Modal show={show} onHide={() => onClose()} size="lg">
        <Modal.Header closeButton className="border-bottom">
          <Modal.Title>Paramètres de Paiement</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-white">
          <div className="d-flex align-items-center justify-content-between flex-wrap border mb-3 p-3 pb-0 rounded bg-white">
            <div className="mb-3">
              <h6 className="mb-1">Vues</h6>
              <p className="mb-0 text-muted">
                Basculez entre le parametrage des paiements et des signataires.
              </p>
            </div>
            <div className="mb-3">
              <div className="btn-group">
                <button
                  type="button"
                  className={`btn btn-sm ${
                    settingsView === "payment"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setSettingsView("payment")}
                >
                  Paiements
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    settingsView === "signatories"
                      ? "btn-primary"
                      : "btn-outline-primary"
                  }`}
                  onClick={() => setSettingsView("signatories")}
                >
                  Signataires
                </button>
              </div>
            </div>
          </div>
          <div className="d-block">
            {settingsView === "payment" && (
              <>
                <div className="d-flex align-items-center justify-content-between flex-wrap border mb-3 p-3 pb-0 rounded bg-white">
                  {/* Partie gauche : icône + texte */}
                  <div className="d-flex align-items-center mb-3">
                    <span className="flex-shrink-0 me-2 border rounded p-2 d-flex align-items-center justify-content-center">
                      <ImageWithBasePath
                        src="assets/img/icons/connected-app-01.svg"
                        alt="Img"
                      />
                    </span>
                    <div>
                      <h6>Devise</h6>
                      <p className="mb-0">Veuillez mentionner la devise.</p>
                    </div>
                  </div>

                  {/* Partie droite : input + bouton */}
                  <div className="d-flex align-items-center mb-3 gap-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Ex: USD"
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      style={{ minWidth: "100px" }}
                    />
                    <ProtectedBtnUpdateSettingsCurrency
                      onSubmitCurrency={onSubmitCurrency}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-between flex-wrap border mb-3 p-3 pb-0 rounded bg-white">
                  {/* Titre + bouton Nouveau */}
                  <div className="d-flex align-items-center justify-content-between w-100 mb-3">
                    <h6 className="mb-0">Comptes Bancaires</h6>
                    <ProtectedBtnAddAccount
                      onClose={onClose}
                      setShowCreate={setShowCreate}
                    />
                  </div>

                  {/* /Bank Accounts Grid */}
                  {loadingAccounts ? (
                    <div className="text-center w-100">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Chargement...</span>
                      </div>
                    </div>
                  ) : bankAccounts.length > 0 ? (
                    bankAccounts.map((account: any, idx: number) => (
                      <div className="container" key={idx}>
                        <div className="col-12 d-flex">
                          <div className="card flex-fill bg-warning-transparent-subtle border border-1 shadow-md">
                            <div className="card-body p-1">
                              {/* Bloc NOM avec fond vert */}
                              <div className="rounded-2 p-2 d-flex align-items-center justify-content-between">
                                <div className="d-flex align-items-center">
                                  <div className="avatar avatar-lg flex-shrink-0 bg-info-subtle d-flex align-items-center justify-content-center rounded-circle">
                                    <i className="ti ti-wallet text-info fs-1" />
                                  </div>
                                  <div className="mx-2 border-end border-2 border-primary px-2 h-100">
                                    <h5>
                                      {account?.account_title ??
                                        " Compte Bancaire"}
                                    </h5>
                                  </div>
                                  <div className="ms-2">
                                    <h5 className="mb-0">
                                      Banque :{" "}
                                      {account?.bank_name ?? " Inconnue"}
                                    </h5>
                                    <p className="mb-0 text-muted">
                                      N° :{" "}
                                      {account?.account_number ??
                                        " Non définis"}
                                    </p>
                                  </div>
                                </div>

                                <div className="dropdown">
                                  <Link
                                    to="#"
                                    className="btn btn-white btn-icon btn-sm d-flex align-items-center justify-content-center rounded-circle p-0"
                                    data-bs-toggle="dropdown"
                                    aria-expanded="false"
                                  >
                                    <i className="ti ti-dots-vertical fs-14" />
                                  </Link>
                                  <ul className="dropdown-menu dropdown-menu-right p-3">
                                    <li>
                                      <ProtectedBtnUpdateAccount
                                        setSelectedAccount={setSelectedAccount}
                                        setShow={setShow}
                                        setShowEdit={setShowEdit}
                                        account={account}
                                      />
                                    </li>
                                    <li>
                                      <ProtectedBtnDeleteAccount
                                        showConfirmationAlert={
                                          showConfirmationAlert
                                        }
                                        account={account}
                                      />
                                    </li>
                                  </ul>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center">
                      <p className="text-muted">
                        Aucun compte bancaire trouvé.
                      </p>
                    </div>
                  )}
                </div>
              </>
            )}

            {settingsView === "signatories" && (
              <form
                className="d-flex align-items-center justify-content-between flex-wrap border mb-3 p-3 pb-0 rounded bg-white"
                onSubmit={handleSubmitSignatory(onSubmitSignatories)}
              >
                <div className="d-flex align-items-center justify-content-between w-100 mb-3">
                  <h6 className="mb-1">Signataires des documents</h6>
                  {/* boutton proteger pour modifier les parametres des signataires */}
                  <ProtectedBtnUpdateSettingsSignatory
                    signatorySubmitting={signatorySubmitting}
                  />
                </div>

                <div className="row w-100">
                  <div className="col-12">
                    <h6 className="mb-2">Bourses/Remises</h6>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Nom signataire 1</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.bourseSignatory1
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Dr Boubacar Sanogo"
                          {...registerSignatory("bourseSignatory1")}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-user-circle" />
                        </span>
                        {signatoryErrors.bourseSignatory1 && (
                          <div className="invalid-feedback">
                            {signatoryErrors.bourseSignatory1.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Fonction signataire 1
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.bourseSignatory1Post
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Recteur"
                          {...registerSignatory("bourseSignatory1Post")}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-briefcase" />
                        </span>
                        {signatoryErrors.bourseSignatory1Post && (
                          <div className="invalid-feedback">
                            {signatoryErrors.bourseSignatory1Post.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Nom signataire 2</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.bourseSignatory2
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Fatoumata Traore"
                          {...registerSignatory("bourseSignatory2")}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-user-circle" />
                        </span>
                        {signatoryErrors.bourseSignatory2 && (
                          <div className="invalid-feedback">
                            {signatoryErrors.bourseSignatory2.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">
                        Fonction signataire 2
                      </label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.bourseSignatory2Post
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Directeur financier"
                          {...registerSignatory("bourseSignatory2Post")}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-briefcase" />
                        </span>
                        {signatoryErrors.bourseSignatory2Post && (
                          <div className="invalid-feedback">
                            {signatoryErrors.bourseSignatory2Post.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 mt-2">
                    <h6 className="mb-2">Recu de paiement</h6>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Nom signataire</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.receiptSignatory1
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Ibrahim Keita"
                          {...registerSignatory("receiptSignatory1")}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-user-circle" />
                        </span>
                        {signatoryErrors.receiptSignatory1 && (
                          <div className="invalid-feedback">
                            {signatoryErrors.receiptSignatory1.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Fonction signataire</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.receiptSignatory1Post
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Comptable"
                          {...registerSignatory("receiptSignatory1Post")}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-briefcase" />
                        </span>
                        {signatoryErrors.receiptSignatory1Post && (
                          <div className="invalid-feedback">
                            {signatoryErrors.receiptSignatory1Post.message}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 mt-2">
                    <h6 className="mb-2">Rapport Annuel Bourse / Remise</h6>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Nom signataire</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.annualReportBourseSignatory1
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Ibrahim Keita"
                          {...registerSignatory("annualReportBourseSignatory1")}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-user-circle" />
                        </span>
                        {signatoryErrors.annualReportBourseSignatory1 && (
                          <div className="invalid-feedback">
                            {
                              signatoryErrors.annualReportBourseSignatory1
                                .message
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Fonction signataire</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.annualReportBourseSignatory1Post
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Comptable"
                          {...registerSignatory(
                            "annualReportBourseSignatory1Post",
                          )}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-briefcase" />
                        </span>
                        {signatoryErrors.annualReportBourseSignatory1Post && (
                          <div className="invalid-feedback">
                            {
                              signatoryErrors.annualReportBourseSignatory1Post
                                .message
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="col-12 mt-2">
                    <h6 className="mb-2">Rapport Financier</h6>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Nom signataire</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.financialAnnualReportSignatory1
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Ibrahim Keita"
                          {...registerSignatory(
                            "financialAnnualReportSignatory1",
                          )}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-user-circle" />
                        </span>
                        {signatoryErrors.financialAnnualReportSignatory1 && (
                          <div className="invalid-feedback">
                            {
                              signatoryErrors.financialAnnualReportSignatory1
                                .message
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Fonction signataire</label>
                      <div className="input-group">
                        <input
                          type="text"
                          className={
                            signatoryErrors.financialAnnualReportSignatory1Post
                              ? "form-control is-invalid"
                              : "form-control"
                          }
                          placeholder="Ex: Comptable"
                          {...registerSignatory(
                            "financialAnnualReportSignatory1Post",
                          )}
                        />
                        <span className="input-group-text">
                          <i className="ti ti-briefcase" />
                        </span>
                        {signatoryErrors.financialAnnualReportSignatory1Post && (
                          <div className="invalid-feedback">
                            {
                              signatoryErrors
                                .financialAnnualReportSignatory1Post.message
                            }
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <button
            type="button"
            className="btn btn-light me-3"
            onClick={() => {
              onClose();
            }}
          >
            Fermer
          </button>
        </Modal.Footer>
      </Modal>

      <CreateAccountModal
        show={showCreate}
        id_paymentSetting={paymentSettings?.id}
        onClose={() => {
          setShow(true);
          setShowCreate(false);
        }}
        onSaved={reloadAccountData}
      />
      <EditAccountModal
        show={showEdit}
        account={selectedAccount}
        onClose={() => {
          setShow(true);
          setShowEdit(false);
        }}
        onSaved={reloadAccountData}
      />
    </>
  );
};

export default withPermission(
  PaymentSettingsModal,
  [
    ...Object.values(Action).map((action) =>
      buildPermission(Resource.PAYMENT_SETTING, action),
    ),
  ],
  "masquer",
);
